import React, { useState, useRef } from "react";
import { Upload, FileSpreadsheet, FileText, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { authApiService } from "../../apis/authService";
import toast from "react-hot-toast";

export function ICD10CodeUpload() {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = [
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'application/vnd.ms-excel.sheet.macroEnabled.12',
        'text/csv',
        'application/csv',
        'text/plain'
      ];
      const allowedExtensions = ['.xlsx', '.xls', '.xlsm', '.csv'];
      const fileExtension = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(fileExtension)) {
        toast.error('Invalid file type. Please upload an Excel (.xlsx, .xls, .xlsm) or CSV (.csv) file.');
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        toast.error('File size too large. Maximum size is 10MB.');
        return;
      }

      setFile(selectedFile);
      setUploadResult(null);
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file to upload');
      return;
    }

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('excelFile', file);
      const response = await authApiService.post('/precharting/icd10/import-excel', formData, {
        headers: {
          'Content-Type': undefined,
        },
      });

      // Handle response
      const result = response.data?.body || response.data;
      
      if (result.success) {
        setUploadResult({
          success: true,
          summary: result.data?.summary || {},
          added: result.data?.added || [],
          skipped: result.data?.skipped || [],
          errors: result.data?.errors || [],
          skippedCount: result.data?.skippedCount || 0
        });
        toast.success(result.message || 'ICD-10 codes imported successfully');
        
        // Reset file after successful upload
        setFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } else {
        setUploadResult({
          success: false,
          message: result.message || 'Upload failed'
        });
        toast.error(result.message || 'Failed to import ICD-10 codes');
      }
    } catch (error) {
      console.error('Upload error:', error);
      const errorMessage = error.response?.data?.body?.message || 
                          error.response?.data?.message || 
                          error.message || 
                          'Failed to upload file';
      setUploadResult({
        success: false,
        message: errorMessage
      });
      toast.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getFileIcon = () => {
    if (!file) return null;
    const extension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (['.xlsx', '.xls', '.xlsm'].includes(extension)) {
      return <FileSpreadsheet className="h-8 w-8 text-green-600" />;
    }
    return <FileText className="h-8 w-8 text-blue-600" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(2) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 border-2 border-gray-300">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2 flex items-center gap-2">
          <FileSpreadsheet className="h-6 w-6 text-blue-600" />
          ICD-10 Code Import
        </h2>
        <p className="text-gray-600">
          Upload Excel (.xlsx, .xls, .xlsm) or CSV (.csv) files to bulk import ICD-10-CM codes into the database.
          Only new codes that don't already exist will be added.
        </p>
      </div>

      {/* File Upload Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select File
        </label>
        <div className="flex items-center gap-4">
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls,.xlsm,.csv"
              onChange={handleFileSelect}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="flex items-center gap-3 px-4 py-3 border-2 border-dashed border-gray-400 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-50 transition-colors"
            >
              <Upload className="h-5 w-5 text-gray-500" />
              <span className="text-sm text-gray-700">
                {file ? file.name : 'Click to select Excel or CSV file'}
              </span>
            </label>
          </div>
        </div>

        {file && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg flex items-center gap-4">
            {getFileIcon()}
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{file.name}</p>
              <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
            </div>
            <button
              onClick={handleClear}
              className="text-red-600 hover:text-red-800 text-sm font-medium"
            >
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Upload Button */}
      <div className="mb-6">
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-colors"
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Uploading...
            </>
          ) : (
            <>
              <Upload className="h-5 w-5" />
              Upload and Import Codes
            </>
          )}
        </button>
      </div>

      {/* File Format Instructions */}
      <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h3 className="text-sm font-semibold text-blue-900 mb-2">File Format Requirements:</h3>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• <strong>Excel format:</strong> Column A = Code, Column B = Description</li>
          <li>• <strong>CSV format:</strong> First row should be headers (Code, Description)</li>
          <li>• <strong>Maximum file size:</strong> 10MB</li>
          <li>• <strong>Supported formats:</strong> .xlsx, .xls, .xlsm, .csv</li>
          <li>• Only valid ICD-10-CM codes will be processed</li>
          <li>• Codes already in the database will be skipped</li>
        </ul>
      </div>

      {/* Upload Results */}
      {uploadResult && (
        <div className={`rounded-lg p-6 border-2 ${
          uploadResult.success 
            ? 'bg-green-50 border-green-300' 
            : 'bg-red-50 border-red-300'
        }`}>
          {uploadResult.success ? (
            <>
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
                <h3 className="text-lg font-semibold text-green-900">Upload Successful!</h3>
              </div>

              {uploadResult.summary && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Total in File</p>
                    <p className="text-lg font-bold text-gray-900">
                      {uploadResult.summary.totalInFile || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Added</p>
                    <p className="text-lg font-bold text-green-600">
                      {uploadResult.summary.added || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Skipped</p>
                    <p className="text-lg font-bold text-yellow-600">
                      {uploadResult.summary.skipped || 0}
                    </p>
                  </div>
                  <div className="bg-white rounded p-3 text-center">
                    <p className="text-xs text-gray-600">Errors</p>
                    <p className="text-lg font-bold text-red-600">
                      {uploadResult.summary.errors || 0}
                    </p>
                  </div>
                </div>
              )}

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-4">
                  <h4 className="text-sm font-semibold text-red-900 mb-2">Errors:</h4>
                  <div className="max-h-40 overflow-y-auto bg-white rounded p-3">
                    {uploadResult.errors.slice(0, 10).map((error, index) => (
                      <div key={index} className="text-xs text-red-700 mb-1">
                        Row {error.rowNumber}: {error.code} - {error.error || error.reason}
                      </div>
                    ))}
                    {uploadResult.errors.length > 10 && (
                      <p className="text-xs text-gray-500 mt-2">
                        ... and {uploadResult.errors.length - 10} more errors
                      </p>
                    )}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center gap-2">
              <XCircle className="h-6 w-6 text-red-600" />
              <div>
                <h3 className="text-lg font-semibold text-red-900">Upload Failed</h3>
                <p className="text-sm text-red-700 mt-1">{uploadResult.message}</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

